import { useEffect, useState } from 'react';
import { IEarningFormData, IRuleFormData } from '../types/interfaces/CampaingTypes';
import { CampaignManagementQuery } from '../../../services/query';
import { CampaignManagementCommand } from '../../../services/command';
import { DropResult } from 'react-beautiful-dnd';
import {
    RuleFormInput,
    RuleType,
    RuleCondition,
    RuleAndEarningModalNames,
    EarningValue,
    EarningFormInput,
    EarningApplicationScopeType,
    EarningProductDiscountScopeType,
    EarningCalculationDiscountType,
    EarningOptionalSetting,
    EarningPriceSortingType,
    EarningDistributionType,
    RuleListCondition,
    RuleNumberCondition,
    RuleBooleanCondition,
    RuleExecutionType,
} from '../../../types/enums/campaign-enums';
import { enumSnackbarStatusType, Region } from '../../../types/enums';
import { useTranslation } from 'react-i18next';
import Helper from '../../../utils/helper';
import { showSnackbar } from '../../../stores/redux/actions';
import { useDispatch } from 'react-redux';

export const StepEarningAndRuleServiceComponent = (props: any) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const campaignService = props.campaignService;
    const {
        showLoading,
        hideLoading,
        advanceToNextStep,
        downloadFile,
        updatedCachedVersion,
        setUpdatedCacheVersion,
        getRuleCustomerTypeValuesByRegion,
        getRuleDeliveryMethodValuesByRegion,
        getRuleShippingCompanyValuesByRegion,
        getRulePaymentMethodValuesByRegion,
        getRulePaymentCollectorValuesByRegion,
        getRulePlatformValuesByRegion,
        saveCampaignForm,
    } = campaignService;

    const [errors, setErrors] = useState({});

    const [rules, setRules] = useState<any[]>([] as any[]);
    const [earnings, setEarnings] = useState<any[]>([]);

    const [modalInfo, setModalInfo] = useState<any>({
        name: '',
        open: false,
        editMode: false,
        formData: null,
    });

    const isUsedCondition = (filterTypeId: number, conditionId: number) => {
        if (modalInfo.formData == null) return false;

        const ruleId = modalInfo.formData.id;
        const rule = ruleId != undefined || ruleId == 0 ? rules.find((x: any) => x.id == ruleId) : null;
        if (rule == null) return false;

        const filters = rule.campaignFilters?.filter(
            (x: any) => x.id != modalInfo.formData.ruleConditionId && x.campaignFilterTypeId == filterTypeId
        );

        let result = false;
        switch (conditionId) {
            case RuleCondition.EqualsThan:
                result = filters.some(
                    (x: any) =>
                        x.conditionId == RuleCondition.EqualsThan ||
                        x.conditionId == RuleCondition.GreaterThan ||
                        x.conditionId == RuleCondition.LessThan
                );
                break;
            case RuleCondition.GreaterThan:
                result = filters.some(
                    (x: any) => x.conditionId == RuleCondition.EqualsThan || x.conditionId == RuleCondition.GreaterThan
                );
                break;
            case RuleCondition.LessThan:
                result = filters.some(
                    (x: any) => x.conditionId == RuleCondition.EqualsThan || x.conditionId == RuleCondition.LessThan
                );
                break;
            default:
                result = filters.some((x: any) => x.conditionId == conditionId);
                break;
        }

        if (result && modalInfo.editMode == true && modalInfo.formData.condition == conditionId) {
            result = false;
        }

        return result;
    };

    let PageObjects: any = {
        Rule: {
            name: RuleAndEarningModalNames.Rule,
            formInput: RuleFormInput,
            initialData: {
                id: 0,
                ruleConditionId: 0,
                campaignRuleId: parseInt(campaignService?.id ?? '0'),
                ruleName: '',
                ruleType: null,
                condition: null,
                valueSelect: null,
                valueNumber: null,
                valueSwitch: 'false',
                valueFile: null,
                valueFileName: '',
                status: 0,
                message: '',
            } as IRuleFormData,
            ruleTypes: RuleType,
            conditions: RuleCondition,
            ruleTypeObjects: [
                {
                    id: RuleType.CustomerType,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.customerType'),
                    conditions: RuleListCondition.map((x: any) => ({
                        ...x,
                        disabled: isUsedCondition(RuleType.CustomerType, x.id),
                    })),
                    values: getRuleCustomerTypeValuesByRegion(),
                },
                {
                    id: RuleType.Platform,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.platform'),
                    conditions: RuleListCondition.map((x: any) => ({
                        ...x,
                        disabled: isUsedCondition(RuleType.Platform, x.id),
                    })),
                    values: getRulePlatformValuesByRegion(),
                },
                {
                    id: RuleType.DeliveryMethod,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.deliveryMethod'),
                    conditions: RuleListCondition.map((x: any) => ({
                        ...x,
                        disabled: isUsedCondition(RuleType.DeliveryMethod, x.id),
                    })),
                    values: getRuleDeliveryMethodValuesByRegion(),
                },
                {
                    id: RuleType.UsageLimit,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.usageLimit'),
                    conditions: RuleNumberCondition.map((x: any) => ({
                        ...x,
                        disabled: isUsedCondition(RuleType.UsageLimit, x.id),
                    })),
                },
                {
                    id: RuleType.CustomerInformation,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.customerInformation'
                    ),
                    conditions: RuleListCondition.map((x: any) => ({
                        ...x,
                        disabled: isUsedCondition(RuleType.CustomerInformation, x.id),
                    })),
                },
                {
                    id: RuleType.ShippingCompany,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.shippingCompany'),
                    conditions: RuleListCondition.map((x: any) => ({
                        ...x,
                        disabled: isUsedCondition(RuleType.ShippingCompany, x.id),
                    })),
                    values: getRuleShippingCompanyValuesByRegion(),
                },
                {
                    id: RuleType.OrderAmount,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.orderAmount'),
                    conditions: RuleNumberCondition.map((x: any) => ({
                        ...x,
                        disabled: isUsedCondition(RuleType.OrderAmount, x.id),
                    })),
                },
                {
                    id: RuleType.PaymentMethod,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.paymentMethod'),
                    conditions: RuleListCondition.map((x: any) => ({
                        ...x,
                        disabled: isUsedCondition(RuleType.PaymentMethod, x.id),
                    })),
                    values: getRulePaymentMethodValuesByRegion(),
                },
                {
                    id: RuleType.Quantity,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.quantity'),
                    conditions: RuleNumberCondition.map((x: any) => ({
                        ...x,
                        disabled: isUsedCondition(RuleType.Quantity, x.id),
                    })),
                },
                {
                    id: RuleType.AtFirstOrder,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.atFirstOrder'),
                    conditions: RuleBooleanCondition,
                },
                // {
                //   id: RuleType.OnlyNewCustomers,
                //   name: t("campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.onlyNewCustomers"),
                //   conditions: RuleBooleanCondition,
                // },
                // {
                //   id: RuleType.CreditCardType,
                //   name: t("campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.creditCardType"),
                //   conditions: RuleListCondition.map((x: any) => ({
                //     ...x,
                //     disabled: isUsedCondition(RuleType.CreditCardType, x.id),
                //   })),
                //   values: getRuleCreditCardTypeValuesByRegion(),
                // },
                {
                    id: RuleType.NewsCustomerMustJoinedAfterStartDate,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.newCustomerMustJoinedAfterStartDate'
                    ),
                    conditions: RuleBooleanCondition,
                },
                {
                    id: RuleType.Option,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.option'),
                    conditions: RuleListCondition.map((x: any) => ({
                        ...x,
                        disabled: isUsedCondition(RuleType.Option, x.id),
                    })),
                },
                {
                    id: RuleType.Store,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.storePickup'),
                    conditions: RuleListCondition.map((x: any) => ({
                        ...x,
                        disabled: isUsedCondition(RuleType.Store, x.id),
                    })),
                    enabledRegions: [Region.Kazakhistan, Region.Turkey],
                },
                {
                    id: RuleType.PaymentCollector,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.paymentCollector'),
                    conditions: RuleListCondition.map((x: any) => ({
                        ...x,
                        disabled: isUsedCondition(RuleType.PaymentCollector, x.id),
                    })),
                    values: getRulePaymentCollectorValuesByRegion(),
                    enabledRegions: [Region.Kazakhistan],
                },
                {
                    id: RuleType.CouponMethod,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.ruleTypeOption.couponMethod'),
                    conditions: RuleListCondition.map((x: any) => ({
                        ...x,
                        disabled: isUsedCondition(RuleType.CouponMethod, x.id),
                    })),
                },
            ],
            conditionObjects: [
                {
                    id: RuleCondition.Includes,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.conditionOption.include'),
                },
                {
                    id: RuleCondition.Excludes,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.conditionOption.exclude'),
                },
                {
                    id: RuleCondition.GreaterThan,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.conditionOption.greaterEqualThan'),
                },
                {
                    id: RuleCondition.LessThan,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.conditionOption.lessEqualThan'),
                },
                {
                    id: RuleCondition.EqualsThan,
                    name: t('campaignManagement.save.step.rulesAndEarnings.rule.form.conditionOption.equals'),
                },
            ],
            inputPermissions: [
                {
                    key: RuleFormInput.Condition,
                    show: [
                        RuleType.CustomerType,
                        RuleType.Platform,
                        RuleType.DeliveryMethod,
                        RuleType.UsageLimit,
                        RuleType.CustomerInformation,
                        RuleType.ShippingCompany,
                        RuleType.OrderAmount,
                        RuleType.PaymentMethod,
                        RuleType.Quantity,
                        RuleType.CreditCardType,
                        RuleType.Option,
                        RuleType.Store,
                        RuleType.PaymentCollector,
                        RuleType.CouponMethod,
                    ],
                },
                {
                    key: RuleFormInput.ValueSelect,
                    show: [
                        RuleType.CustomerType,
                        RuleType.Platform,
                        RuleType.DeliveryMethod,
                        RuleType.ShippingCompany,
                        RuleType.PaymentMethod,
                        RuleType.CreditCardType,
                        RuleType.PaymentCollector,
                        RuleType.CouponMethod,
                    ],
                },
                {
                    key: RuleFormInput.ValueNumber,
                    show: [RuleType.UsageLimit, RuleType.OrderAmount, RuleType.Quantity],
                },
                {
                    key: RuleFormInput.ValueSwitch,
                    show: [
                        RuleType.AtFirstOrder,
                        RuleType.OnlyNewCustomers,
                        RuleType.NewsCustomerMustJoinedAfterStartDate,
                    ],
                },
                {
                    key: RuleFormInput.ValueFile,
                    show: [RuleType.CustomerInformation, RuleType.Option, RuleType.Store],
                },
            ],
        },
        Earning: {
            name: RuleAndEarningModalNames.Earning,
            initialData: {
                id: 0,
                campaignRuleId: parseInt(campaignService?.id ?? '0'), //ana kampanya id'si
                campaignFilterRuleId: null, //seçilen kural
                name: '', //kazanım adı
                calculationDiscountType: null, //indirim türü (select)
                value: 0, //indirim değeri (number)
                optionalSetting: [], //sonradan ekledim
                priceSortingType: EarningPriceSortingType.HighToLowPrice, //ürün sıralama (select)
                maxDiscountAmount: 0, //maksimum indirim tutarı (number)
                maxDiscountedProductCount: 0, //maksimum indirimli ürün adeti (number)
                numberOfFreeItems: 0, //ücretsiz ürün adeti (number)
                numberOfItemPurchased: 0, //n.ürün, satın alınacak ürün adeti (number)
                onlyNonDiscountedProducts: true, //sadece indirimsiz ürünler (switch)
                applicationScopeType: null, // kazanım türü (kargo indirim, sipariş indirim, ürün indirim) (select)
                productDiscountScopeType: 0, // kazanım tipi alt başlık (ürüne indirim, n. ürüne indirim, n tane al x öde) (select)
                distributionType: EarningDistributionType.QuantityAndPrice, //indirim dağıtım yöntemi (select)
                useDiscountedPrice: true, // baz fiyat (switch)
                includeShippingPriceToGrandTotal: false, //kargo ücretini dahil et (switch)
                showDiscountedPrice: false, //önyüzde indirimli fiyat göster (switch)
                descriptionTexts: [],
            } as IEarningFormData,
            formInput: EarningFormInput,
            applicationScopeType: EarningApplicationScopeType,
            productDiscountScopeType: EarningProductDiscountScopeType,
            applicationScopeTypeObjects: [
                {
                    id: EarningApplicationScopeType.Shipping,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.applicationScopeTypeOption.shippingDiscount'
                    ),
                    parent: false,
                    child: false,
                },
                {
                    id: EarningApplicationScopeType.Order,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.applicationScopeTypeOption.orderDiscount'
                    ),
                    parent: false,
                    child: false,
                },
                {
                    id: EarningApplicationScopeType.Product,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.applicationScopeTypeOption.productDiscount'
                    ),
                    parent: true,
                    child: false,
                },
                {
                    id: `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToProduct}`,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.applicationScopeTypeOption.discountOnProduct'
                    ),
                    parent: false,
                    child: true,
                },
                {
                    id: `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToNthProducts}`,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.applicationScopeTypeOption.discountForProductN'
                    ),
                    parent: false,
                    child: true,
                },
                {
                    id: `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.BuyNPayY}`,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.applicationScopeTypeOption.buyNPayX'
                    ),
                    parent: false,
                    child: true,
                },
            ],
            calculationDiscountType: EarningCalculationDiscountType,
            calculationDiscountTypeObjects: [
                {
                    id: EarningCalculationDiscountType.Percentage,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.calculationDiscountTypeOption.percentageDiscount'
                    ),
                },
                {
                    id: EarningCalculationDiscountType.StaticDiscount,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.calculationDiscountTypeOption.staticDiscount'
                    ),
                },
                {
                    id: EarningCalculationDiscountType.FixedPrice,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.calculationDiscountTypeOption.fixedPrice'
                    ),
                },
            ],
            optionalSettingObjects: [
                {
                    id: EarningOptionalSetting.MaxDiscountAmount,
                    key: EarningFormInput.MaxDiscountAmount,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.maxDiscountAmount'
                    ),
                    show: [
                        EarningApplicationScopeType.Order.toString(),
                        EarningApplicationScopeType.Shipping.toString(),
                    ],
                },
                {
                    id: EarningOptionalSetting.IncludeShippingPriceToGrandTotal,
                    key: EarningFormInput.IncludeShippingPriceToGrandTotal,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.includeShippingPrice'
                    ),
                    show: [
                        EarningApplicationScopeType.Order.toString(),
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToProduct}`,
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToNthProducts}`,
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.BuyNPayY}`,
                    ],
                },
                {
                    id: EarningOptionalSetting.UseDiscountedPrice,
                    key: EarningFormInput.UseDiscountedPrice,
                    name: t('campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.basePrice'),
                    show: [
                        EarningApplicationScopeType.Order.toString(),
                        EarningApplicationScopeType.Shipping.toString(),
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToProduct}`,
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToNthProducts}`,
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.BuyNPayY}`,
                    ],
                },
                {
                    id: EarningOptionalSetting.OnlyNonDiscountedProducts,
                    key: EarningFormInput.OnlyNonDiscountedProducts,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.onlyNonDiscountedProducts'
                    ),
                    show: [
                        EarningApplicationScopeType.Shipping.toString(),
                        EarningApplicationScopeType.Order.toString(),
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToProduct}`,
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToNthProducts}`,
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.BuyNPayY}`,
                    ],
                },
                {
                    id: EarningOptionalSetting.PriceSortingType,
                    key: EarningFormInput.PriceSortingType,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.productSorting'
                    ),
                    show: [
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToProduct}`,
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToNthProducts}`,
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.BuyNPayY}`,
                    ],
                },
                {
                    id: EarningOptionalSetting.MaxDiscountedProductCount,
                    key: EarningFormInput.MaxDiscountedProductCount,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.maxDiscountedProductCount'
                    ),
                    show: [
                        EarningApplicationScopeType.Order.toString(),
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToProduct}`,
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToNthProducts}`,
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.BuyNPayY}`,
                    ],
                },
                // {
                //   id: EarningOptionalSetting.DistributionType,
                //   key: EarningFormInput.DistributionType,
                //   name: t("campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.discountDistributionType"),
                //   show: [
                //     EarningApplicationScopeType.Order.toString(),
                //     `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToProduct}`,
                //     `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToNthProducts}`,
                //     `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.BuyNPayY}`,
                //   ],
                // },
                {
                    id: EarningOptionalSetting.ShowDiscountedPrice,
                    key: EarningFormInput.ShowDiscountedPrice,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.showDiscountedPrice'
                    ),
                    show: [`${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToProduct}`],
                },
            ],
            BasePrice: EarningValue,
            BasePriceObjects: [
                {
                    id: EarningValue.False,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.basePriceOption.priceWithoutCampaign'
                    ),
                },
                {
                    id: EarningValue.True,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.basePriceOption.priceWithCampaign'
                    ),
                },
            ],
            PriceSortingType: EarningPriceSortingType,
            PriceSortingTypeObjects: [
                {
                    id: EarningPriceSortingType.LowToHighPrice,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.productSortingOption.lowToHighPrice'
                    ),
                },
                {
                    id: EarningPriceSortingType.HighToLowPrice,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.productSortingOption.highToLowPrice'
                    ),
                },
            ],
            DistributionType: EarningDistributionType,
            DistributionTypeObjects: [
                {
                    id: EarningDistributionType.Quantity,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.distributionTypeOption.propotionalToQuantity'
                    ),
                },
                {
                    id: EarningDistributionType.QuantityAndPrice,
                    name: t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.distributionTypeOption.propotionalToQuantityAndPrice'
                    ),
                },
            ],
        },
        RuleBulkCreate: {
            name: RuleAndEarningModalNames.RuleBulkCreate,
            initialData: {
                id: 0,
                campaignRuleId: parseInt(campaignService?.id ?? '0'),
            },
        },
    };

    PageObjects = {
        ...PageObjects,
        RuleCondition: {
            ...PageObjects.Rule,
            name: RuleAndEarningModalNames.RuleCondition,
        },
    };

    //rule
    const getRules = async () => {
        const campaignId: number = parseInt(campaignService?.id ?? '0');
        if (campaignId > 0) {
            showLoading();

            let result = await CampaignManagementQuery.GetRulesByCampaignRuleId({
                campaignRuleId: campaignId,
                culture: Helper.getLang(),
            });

            if (result?.isSuccess && result.data != undefined) {
                setRules(result.data);
            }

            hideLoading();
        }
    };

    const saveRule = async (payload: any) => {
        let result: any = await CampaignManagementCommand.SaveRule(payload);

        let originalRules = JSON.parse(JSON.stringify(rules));
        let changedRules = [...rules];
        let index = changedRules.findIndex((r: any) => r.id == payload.id);
        if (index > -1) {
            changedRules[index].name = payload.name;
        }

        setRules(changedRules);

        const { isSuccess, message } = result;
        if (!isSuccess) {
            dispatch(
                showSnackbar({
                    message,
                    status: enumSnackbarStatusType.danger,
                })
            );
            setRules(originalRules);
        }

        return result;
    };

    const deleteRule = async (id: number) => {
        let result: any = await CampaignManagementQuery.DeleteRuleById({
            Id: id,
        });

        let { isSuccess } = result;
        if (isSuccess) {
            let _rules = [...rules];
            const index = rules.findIndex((x: any) => x.id == id);
            if (index > -1) {
                _rules.splice(index, 1);
            }

            setRules(_rules);

            let _earnings = [...earnings];
            _earnings.map((x: any) => {
                if (x.campaignFilterRuleId == id) {
                    x.campaignFilterRuleId = null;
                }
            });

            setEarnings(_earnings);
        }
    };

    const reUpdateRuleProcessOrder = async (campaignRuleId: number, ruleIds: number[]) => {
        let result: any = await CampaignManagementCommand.ReUpdateRuleProcessOrder({
            campaignRuleId: campaignRuleId,
            campaignFilterRuleIds: ruleIds,
        });

        return result;
    };

    //rule - condition
    const saveRuleCondition = async (payload: any) => {
        let result: any = await CampaignManagementCommand.SaveRuleCondition(payload);

        return result;
    };

    const saveRuleConditionFile = async (payload: any) => {
        let result: any = await CampaignManagementCommand.SaveRuleConditionFile(payload);

        return result;
    };

    const deleteRuleCondition = async (id: any) => {
        let result: any = await CampaignManagementQuery.DeleteRuleConditionById({
            Id: id,
        });

        let { isSuccess } = result;
        if (isSuccess) {
            await getRules();
        }
    };

    //earning
    const getEarnings = async () => {
        const campaignId: number = parseInt(campaignService?.id ?? '0');
        if (campaignId > 0) {
            showLoading();

            let result = await CampaignManagementQuery.GetEarningsByCampaignRuleId({
                campaignRuleId: campaignId,
            });

            if (result?.isSuccess && result.data != undefined) {
                setEarnings(result.data);
            }

            hideLoading();
        }
    };

    const getEarningById = async (id: number) => {
        let result: any = await CampaignManagementQuery.GetEarningById({
            Id: id,
        });

        return result;
    };

    const saveEarning = async (payload: any) => {
        let result: any = await CampaignManagementCommand.SaveEarning(payload);

        if (!result?.isSuccess) {
            dispatch(
                showSnackbar({
                    message: result.message,
                    status: enumSnackbarStatusType.danger,
                })
            );
        }
        return result;
    };

    const deleteEarning = async (id: number) => {
        let result: any = await CampaignManagementQuery.DeleteEarningById({
            Id: id,
        });

        let { isSuccess } = result;
        if (isSuccess) {
            await getEarnings();
        }
    };

    const reUpdateEarningProcessOrder = async (campaignRuleId: number, earningIds: number[]) => {
        let result: any = await CampaignManagementCommand.ReUpdateEarningProcessOrder({
            campaignRuleId: campaignRuleId,
            campaignRuleEarningIds: earningIds,
        });

        return result;
    };

    const setRuleForEarning = async (earningId: number, ruleId: number[]) => {
        let result: any = await CampaignManagementCommand.SetRuleForEarning({
            Id: earningId,
            campaignFilterRuleId: ruleId,
        });

        const { isSuccess, data } = result;
        if (isSuccess) {
            let _earnings = [...earnings];
            const index: number = earnings.findIndex((x: any) => x.id == earningId);
            if (index > -1) {
                _earnings[index].campaignFilterRuleId = data.campaignFilterRuleId;
            }

            setEarnings(_earnings);
        }

        return result;
    };

    useEffect(() => {
        getRules();
        getEarnings();
    }, [campaignService != undefined && campaignService?.id]);

    //modal

    const openModal = (name: string) => {
        const isExist = Object.values(RuleAndEarningModalNames).some((x) => x == name);
        if (isExist)
            setModalInfo({
                ...modalInfo,
                name: name,
                open: true,
                formData: PageObjects[name].initialData,
            });
    };

    const closeModal = (name: string) => {
        const isExist = Object.values(RuleAndEarningModalNames).some((x) => x == name);
        if (isExist)
            setModalInfo({
                ...modalInfo,
                name: '',
                open: false,
                editMode: false,
                formData: null,
            });
    };

    const saveModal = async (name: string, formValue?: any, setIsLoading?: any) => {
        let result: any;
        let payload: any;

        let ruleId = 0;
        let values: any[] = [];

        let applicationScopeTypeValue =
            formValue.applicationScopeType?.value > -1
                ? formValue.applicationScopeType?.value
                : formValue.applicationScopeType?.value?.split('_')[0];
        let productDiscountScopeTypeValue =
            formValue.applicationScopeType?.value > -1
                ? EarningProductDiscountScopeType.None
                : formValue.applicationScopeType?.value?.split('_')[1];

        switch (name) {
            case RuleAndEarningModalNames.Rule:
            case RuleAndEarningModalNames.RuleCondition:
                if (name == RuleAndEarningModalNames.Rule) {
                    payload = {
                        id: formValue.id.value ?? 0,
                        campaignRuleId: parseInt(campaignService.id!),
                        name: formValue.ruleName.value,
                        culture: Helper.getLang(),
                    };

                    result = await saveRule(payload);
                    const { isSuccess, data } = result;
                    if (isSuccess) {
                        ruleId = data?.id;
                    }
                }

                ruleId = formValue.id.value > 0 ? formValue.id.value : ruleId;

                PageObjects.Rule.inputPermissions.map((x: any) => {
                    switch (x.key) {
                        case RuleFormInput.ValueSelect:
                            if (x.show.some((s: any) => s == formValue.ruleType.value)) {
                                formValue.valueSelect.value.map((v: any) => {
                                    values.push({ value: parseInt(v) });
                                });
                            }
                            break;
                        case RuleFormInput.ValueNumber:
                            if (x.show.some((s: any) => s == formValue.ruleType.value))
                                values.push({
                                    value: parseFloat(formValue.valueNumber.value),
                                });
                            break;
                        case RuleFormInput.ValueSwitch:
                            if (x.show.some((s: any) => s == formValue.ruleType.value))
                                values.push({ value: formValue.valueSwitch.value });
                            break;
                        case RuleFormInput.ValueFile:
                            if (x.show.some((s: any) => s == formValue.ruleType.value))
                                values.push({ value: parseInt(formValue.valueFile.value) });
                            break;
                    }
                });

                payload = {
                    id: formValue.ruleConditionId.value,
                    campaignFilterTypeId: formValue.ruleType.value,
                    campaignFilterRuleId: ruleId,
                    conditionId: formValue.condition.value,
                };

                if (
                    formValue.ruleType.value == RuleType.CustomerInformation ||
                    formValue.ruleType.value == RuleType.Option ||
                    formValue.ruleType.value == RuleType.Store
                ) {
                    //with file
                    payload = {
                        ...payload,
                        userFileId: formValue.valueFile.value,
                    };

                    await saveRuleConditionFile(payload);

                    //updated cached version
                    setUpdatedCacheVersion({
                        ...updatedCachedVersion,
                        reloadCampaignRuleProducts: formValue.ruleType.value == RuleType.Option,
                        reloadCampaignRuleEmails: formValue.ruleType.value == RuleType.CustomerInformation,
                        reloadCampaignRuleStores: formValue.ruleType.value == RuleType.Store,
                    });
                } else {
                    //without file
                    payload = {
                        ...payload,
                        values: values,
                    };

                    await saveRuleCondition(payload);
                }

                await getRules();

                break;
            case RuleAndEarningModalNames.Earning:
                payload = {
                    id: formValue.id.value ?? 0,
                    campaignRuleId: parseInt(campaignService.id!),
                    campaignFilterRuleId: formValue.campaignFilterRuleId.value,
                    name: formValue.name.value,
                    calculationDiscountType: formValue.calculationDiscountType.value,
                    value: formValue.value.value,
                    priceSortingType: formValue.priceSortingType.value,
                    maxDiscountAmount: formValue.maxDiscountAmount.value,
                    maxDiscountedProductCount: formValue.maxDiscountedProductCount.value,
                    numberOfFreeItems: formValue.numberOfFreeItems.value,
                    numberOfItemPurchased: formValue.numberOfItemPurchased.value,
                    onlyNonDiscountedProducts: formValue.onlyNonDiscountedProducts.value,
                    applicationScopeType: applicationScopeTypeValue,
                    productDiscountScopeType: productDiscountScopeTypeValue,
                    distributionType: formValue.distributionType.value,
                    useDiscountedPrice: formValue.useDiscountedPrice.value,
                    includeShippingPriceToGrandTotal: formValue.includeShippingPriceToGrandTotal.value,
                    showDiscountedPrice: formValue.showDiscountedPrice.value,
                    culture: Helper.getLang(),
                    badges: formValue.badges || [],
                    descriptionTexts: formValue.descriptionTexts || [],
                };

                result = await saveEarning(payload);

                await getEarnings();

                break;
        }

        setIsLoading(false);

        setModalInfo({
            ...modalInfo,
            name: '',
            open: false,
            editMode: false,
        });

        return result;
    };

    //rule
    const addCondition = (id: number) => {
        let rule: any = rules.find((r: any) => r.id == id);
        if (rule != null && rule != undefined) {
            setModalInfo({
                ...modalInfo,
                name: RuleAndEarningModalNames.RuleCondition,
                open: true,
                editMode: false,
                formData: {
                    ...PageObjects.Rule.initialData,
                    id: rule.id,
                    campaignRuleId: rule.campaignRuleId,
                    ruleName: rule.name,
                } as IRuleFormData,
            });
        }
    };

    const editCondition = (id: number, ruleId: number) => {
        let rule: any = rules.find((r: any) => r.id == ruleId);
        let ruleCondition: any = rule.campaignFilters.find((f: any) => f.id == id);

        if (rule != null && rule != undefined && ruleCondition != null && ruleCondition != undefined) {
            let valueSelect: number[] = [];
            let valueNumber = null;
            let valueSwitch = 'false';
            let valueFile = 0;
            let valueFileName = '';

            PageObjects.RuleCondition.inputPermissions.map((x: any) => {
                switch (x.key) {
                    case RuleFormInput.ValueSelect:
                        if (x.show.some((s: any) => s == ruleCondition.campaignFilterTypeId)) {
                            ruleCondition?.campaignFilterValues?.map((v: any) => {
                                valueSelect.push(parseInt(v.value));
                            });
                        }

                        break;
                    case RuleFormInput.ValueNumber:
                        if (x.show.some((s: any) => s == ruleCondition.campaignFilterTypeId))
                            valueNumber = ruleCondition?.campaignFilterValues?.[0]?.value ?? null;

                        break;
                    case RuleFormInput.ValueSwitch:
                        if (x.show.some((s: any) => s == ruleCondition.campaignFilterTypeId))
                            valueSwitch = ruleCondition?.campaignFilterValues?.[0]?.value ?? 'false';

                        break;
                    case RuleFormInput.ValueFile:
                        if (x.show.some((s: any) => s == ruleCondition.campaignFilterTypeId)) {
                            valueFile = ruleCondition?.userFileId ?? 0;
                            valueFileName = ruleCondition?.userFileName ?? '';
                        }

                        break;
                }
            });

            setModalInfo({
                ...modalInfo,
                name: RuleAndEarningModalNames.RuleCondition,
                open: true,
                editMode: true,
                formData: {
                    id: rule.id,
                    ruleConditionId: ruleCondition?.id,
                    campaignRuleId: rule.campaignRuleId,
                    ruleName: rule.name,
                    ruleType: ruleCondition?.campaignFilterTypeId,
                    condition: ruleCondition?.conditionId,
                    valueSelect: valueSelect,
                    valueNumber: valueNumber,
                    valueSwitch: valueSwitch,
                    valueFile: valueFile,
                    valueFileName: valueFileName,
                    status: ruleCondition?.executionStatus,
                    message: ruleCondition?.message,
                } as IRuleFormData,
            });
        }
    };

    const addRule = (event: any) => {
        event.preventDefault();

        openModal(RuleAndEarningModalNames.Rule);
    };

    const handleBulkRuleCreate = (event: any) => {
        event.preventDefault();

        openModal(RuleAndEarningModalNames.RuleBulkCreate);
    };

    const onDragEndRule = async ({ destination, source }: DropResult) => {
        if (!destination) return;

        const reorderedList = Array.from(rules);
        const [removed] = reorderedList.splice(source.index, 1);
        reorderedList.splice(destination.index, 0, removed);

        setRules(reorderedList);

        let campaignRuleId = parseInt(campaignService.id!);
        let ruleIds = reorderedList.map((r) => r.id);

        const result: any = await reUpdateRuleProcessOrder(campaignRuleId, ruleIds);
        const { isSuccess } = result;
        if (!isSuccess) {
            setRules(rules);
        }
    };

    //earning

    const getOptionalSettings = (earning: any) => {
        let optionalSettings: number[] = [];

        if (earning.maxDiscountAmount != PageObjects.Earning.initialData.maxDiscountAmount) {
            optionalSettings.push(EarningOptionalSetting.MaxDiscountAmount);
        }
        if (
            earning.includeShippingPriceToGrandTotal != PageObjects.Earning.initialData.includeShippingPriceToGrandTotal
        ) {
            optionalSettings.push(EarningOptionalSetting.IncludeShippingPriceToGrandTotal);
        }
        if (earning.useDiscountedPrice != PageObjects.Earning.initialData.useDiscountedPrice) {
            optionalSettings.push(EarningOptionalSetting.UseDiscountedPrice);
        }
        if (earning.onlyNonDiscountedProducts != PageObjects.Earning.initialData.onlyNonDiscountedProducts) {
            optionalSettings.push(EarningOptionalSetting.OnlyNonDiscountedProducts);
        }
        if (earning.priceSortingType != PageObjects.Earning.initialData.priceSortingType) {
            optionalSettings.push(EarningOptionalSetting.PriceSortingType);
        }
        if (earning.maxDiscountedProductCount != PageObjects.Earning.initialData.maxDiscountedProductCount) {
            optionalSettings.push(EarningOptionalSetting.MaxDiscountedProductCount);
        }
        if (earning.distributionType != PageObjects.Earning.initialData.distributionType) {
            optionalSettings.push(EarningOptionalSetting.DistributionType);
        }
        if (earning.showDiscountedPrice != PageObjects.Earning.initialData.showDiscountedPrice) {
            optionalSettings.push(EarningOptionalSetting.ShowDiscountedPrice);
        }

        return optionalSettings;
    };

    const addEarning = (event: any) => {
        event.preventDefault();

        openModal(RuleAndEarningModalNames.Earning);
    };

    const editEarning = async (id: number) => {
        const result = await getEarningById(id);
        const { data: earning, isSuccess } = result;
        if (earning == null || !isSuccess) {
            dispatch(
                showSnackbar({
                    message: t('campaignManagement.save.step.rulesAndEarnings.earning.modal.edit.notFoundMessage'),
                    status: enumSnackbarStatusType.danger,
                })
            );
            return;
        }

        let applicationScopeType =
            earning.applicationScopeType == EarningApplicationScopeType.Product
                ? `${EarningApplicationScopeType.Product}_${earning.productDiscountScopeType}`
                : earning.applicationScopeType;

        let optionalSettings = getOptionalSettings(earning);

        let _formData = {
            ...earning,
            applicationScopeType: applicationScopeType,
            optionalSetting: optionalSettings,
        } as IEarningFormData;

        setModalInfo({
            ...modalInfo,
            name: RuleAndEarningModalNames.Earning,
            open: true,
            editMode: true,
            formData: _formData,
        });
    };

    const onDragEndEarning = async ({ destination, source }: DropResult) => {
        if (!destination) return;

        const reorderedList = Array.from(earnings);
        const [removed] = reorderedList.splice(source.index, 1);
        reorderedList.splice(destination.index, 0, removed);

        setEarnings(reorderedList);

        let campaignRuleId = parseInt(campaignService.id!);
        let earningIds = reorderedList.map((r) => r.id);

        const result: any = await reUpdateEarningProcessOrder(campaignRuleId, earningIds);
        const { isSuccess } = result;
        if (!isSuccess) {
            setEarnings(earnings);
        }
    };

    //earning

    //file

    const downloadFileFilter = async (filterId: number) => {
        try {
            const response: any = await CampaignManagementCommand.DownloadFileFilter({
                filterId: filterId,
            });

            const { isSuccess, data } = response;
            if (!isSuccess) {
                dispatch(
                    showSnackbar({
                        message: t(
                            'campaignManagement.save.step.rulesAndEarnings.rule.form.file.errorMessage.downloadErrorMessage'
                        ),
                        status: enumSnackbarStatusType.danger,
                    })
                );

                return false;
            }

            downloadFile(data);
        } catch (error) {
            dispatch(
                showSnackbar({
                    message: `${t(
                        'campaignManagement.save.step.rulesAndEarnings.rule.form.file.errorMessage.downloadErrorMessage'
                    )} ${error}`,
                    status: enumSnackbarStatusType.danger,
                })
            );
        }
    };

    const downloadSlaveExcelFileFilter = async (filterId: number) => {
        try {
            const response: any = await CampaignManagementCommand.DownloadSlaveExcelFileFilter({
                filterId: filterId,
            });

            const { isSuccess, data } = response;
            if (!isSuccess) {
                dispatch(
                    showSnackbar({
                        message: t(
                            'campaignManagement.save.step.rulesAndEarnings.rule.form.file.errorMessage.slaveDownloadFailed'
                        ),
                        status: enumSnackbarStatusType.danger,
                    })
                );

                return false;
            }

            downloadFile(data);
        } catch (error) {
            dispatch(
                showSnackbar({
                    message: `${t(
                        'campaignManagement.save.step.rulesAndEarnings.rule.form.file.errorMessage.slaveDownloadFailed'
                    )} ${error}`,
                    status: enumSnackbarStatusType.danger,
                })
            );
        }
    };

    //form

    const validateEarning = (errors: any) => {
        const withoutRuleList = earnings.filter((x: any) => x.campaignFilterRuleId == null);

        withoutRuleList.map((x: any) => {
            const inputName = `ruleSelect${x.id}`;
            errors[inputName] = t(
                'campaignManagement.save.step.rulesAndEarnings.earning.form.validationMessage.required'
            );
        });

        return errors;
    };

    const onValidate = () => {
        const errors = {} as any;

        validateEarning(errors);

        return errors;
    };

    const saveButtonDisabled =
        rules.length == 0 ||
        earnings.length == 0 ||
        !rules.some((x: any) => x.campaignFilters.length >= 1) ||
        rules.some((x: any) => x.campaignFilters.some((y: any) => y.executionStatus == RuleExecutionType.Queue));

    const saveForm = async (event: any) => {
        event.preventDefault();

        const validateResult = onValidate();
        setErrors(validateResult);

        if (Object.keys(validateResult).length > 0) return;
        if (saveButtonDisabled) return;

        const saveResult = await saveCampaignForm({});
        if (saveResult) {
            advanceToNextStep();
        }
    };

    const saveStep = async () => {
        const validateResult = onValidate();
        setErrors(validateResult);

        if (Object.keys(validateResult).length > 0) return;
        if (saveButtonDisabled) return;

        await saveCampaignForm({});
    };

    return {
        ...campaignService,
        errors,
        setErrors,
        rules,
        earnings,
        modalInfo,
        PageObjects,
        openModal,
        closeModal,
        saveModal,
        addCondition,
        editCondition,
        getRules,
        addRule,
        saveRule,
        handleBulkRuleCreate,
        deleteRule,
        saveRuleCondition,
        saveRuleConditionFile,
        deleteRuleCondition,
        deleteEarning,
        setRuleForEarning,
        onDragEndRule,
        addEarning,
        editEarning,
        onDragEndEarning,
        downloadFileFilter,
        downloadSlaveExcelFileFilter,
        onValidate,
        saveButtonDisabled,
        saveForm,
        saveStep,
        saveCampaignForm,
    };
};

export const StepRulesTimerComponent = ({ ...props }) => {
    const { modalInfo, rules, getRules } = props;

    const RetryTimeValue = 5 * 1000; //default 5 sn

    const isFileProcessing = () => {
        const ruleTypes = [RuleType.CustomerInformation, RuleType.Option, RuleType.Store];

        const result = rules.some((c: any) =>
            c.campaignFilters.some((x: any) => {
                return (
                    ruleTypes.includes(x.campaignFilterTypeId) &&
                    x.userFileId != null &&
                    x.executionStatus == RuleExecutionType.Queue
                );
            })
        );

        return result;
    };

    useEffect(() => {
        if (rules.length > 0) {
            isFileProcessing();
        }
    }, [rules]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            if (!modalInfo.open && isFileProcessing()) {
                getRules();
            }
        }, RetryTimeValue);

        return () => clearInterval(intervalId);
    }, [modalInfo.open && isFileProcessing()]);

    return <></>;
};
